import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  ArrowRight,
  ArrowUpRight,
  Truck,
  Shield,
  Leaf,
  MapPin,
  Phone,
  Clock,
  CheckCircle2,
  Package,
  X,
  ChevronRight,
  ChevronLeft,
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { loadPricingSettings, DEFAULT_PRICING } from '@/components/Admin/PriceManager';
import type { PricingSettings } from '@/components/Admin/PriceManager';
import { LANDING_IMAGES, GRADE_IMAGES, FEATURE_IMAGES, PHOTO_CREDITS } from '@/lib/landingImages';

const LAST_TRACKING_KEY = 'gv:lastTrackingCode';

// ─── "Get started" gallery — grades + sourcing imagery ──────────────────────
const GALLERY = [
  { id: 'red-md', no: '01', label: 'Red Medium', caption: 'Everyday grade' },
  { id: 'red-lg', no: '02', label: 'Red Large', caption: 'Bulk & restaurants' },
  { id: 'red-sm', no: '03', label: 'Red Small', caption: 'Markets & stalls' },
  { id: 'red-md', no: '04', label: 'Direct sourcing', caption: 'Rift Valley farms' },
] as const;

// ─── Delivery zones ────────────────────────────────────────────────────────
const ZONES_PRIMARY = ['Gikomba', 'Eastleigh', 'Westlands', 'Umoja', 'Githurai', 'Kasarani'];
const ZONES_SECONDARY = ['Embakasi', 'Buruburu', 'Ngong Road', 'South B', 'Ruaraka', 'Roysambu', 'Pipeline', 'Langata', 'Parklands'];

const STATS = [
  { value: '500+', label: 'Repeat clients' },
  { value: '12k+', label: 'Orders delivered' },
  { value: '34', label: 'Delivery zones' },
  { value: '< 4 h', label: 'Average delivery' },
];

const FEATURES = [
  { icon: Leaf, img: FEATURE_IMAGES.farm, title: 'Direct from the farm', body: 'Sourced from growers in the Rift Valley and Central Kenya — no brokers, no markups.' },
  { icon: Truck, img: FEATURE_IMAGES.delivery, title: 'Same-day delivery', body: 'Order before 10 AM and we deliver across Nairobi the same day.' },
  { icon: Shield, img: FEATURE_IMAGES.quality, title: 'Quality guarantee', body: 'Bulbs not up to standard? We replace or refund — no questions asked.' },
];

const HOW_IT_WORKS = [
  { step: '01', title: 'Place your order', body: 'Pick the grade, set the kilos. Minimum 5 kg.' },
  { step: '02', title: 'We confirm by call', body: 'Our team rings you back within an hour to lock the delivery slot.' },
  { step: '03', title: 'Same-day delivery', body: 'Pay cash on arrival. Quality guaranteed or replaced.' },
];

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const [pricing, setPricing] = useState<PricingSettings>(DEFAULT_PRICING);
  const [lastTrackingCode, setLastTrackingCode] = useState<string>('');
  const galleryRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getDocs(collection(db, 'product_photos')).then(snap => {
      const photos: Record<string, string> = {};
      snap.forEach(d => { photos[d.id] = d.data().photoUrl || ''; });
      setProductPhotos(photos);
    }).catch(() => {});

    loadPricingSettings().then(setPricing);

    try {
      const code = localStorage.getItem(LAST_TRACKING_KEY);
      if (code) setLastTrackingCode(code);
    } catch { /* private mode */ }
  }, []);

  const dismissRecentOrder = () => {
    try { localStorage.removeItem(LAST_TRACKING_KEY); } catch { /* ignore */ }
    setLastTrackingCode('');
  };

  const heroPhoto = productPhotos['red-md'] || productPhotos['red-lg'] || productPhotos['red-sm'] || LANDING_IMAGES.hero;

  const scrollGallery = (dir: -1 | 1) => {
    galleryRef.current?.scrollBy({ left: dir * 320, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 selection:bg-emerald-900 selection:text-white">

      {/* ─── Navbar ─── */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-3 shrink-0">
            <img src="/brand/logo-icon.png" alt="Genvitungu" className="w-9 h-9 rounded-lg object-cover" />
            <div className="leading-none">
              <p className="font-black text-[18px] tracking-tight text-slate-900">Genvitungu</p>
              <p className="text-[11px] text-slate-500 mt-0.5 tracking-wide uppercase">Red onions, Nairobi</p>
            </div>
          </Link>

          {/* Centre pill nav (reference style) */}
          <nav className="hidden md:flex items-center gap-1 text-sm font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-full px-1.5 py-1.5">
            <a href="#products" className="px-4 py-1.5 rounded-full hover:bg-white hover:text-emerald-900 hover:shadow-sm transition-colors">Products</a>
            <a href="#how" className="px-4 py-1.5 rounded-full hover:bg-white hover:text-emerald-900 hover:shadow-sm transition-colors">How it works</a>
            <a href="#zones" className="px-4 py-1.5 rounded-full hover:bg-white hover:text-emerald-900 hover:shadow-sm transition-colors">Delivery</a>
            <Link to="/track" className="px-4 py-1.5 rounded-full hover:bg-white hover:text-emerald-900 hover:shadow-sm transition-colors">Track order</Link>
          </nav>

          <div className="flex items-center gap-2 shrink-0">
            <Link
              to="/admin"
              className="hidden md:inline-flex text-slate-400 hover:text-slate-700 text-xs font-medium transition-colors px-2"
            >
              Staff
            </Link>
            <button
              onClick={() => navigate('/order')}
              className="inline-flex items-center gap-1.5 bg-emerald-900 hover:bg-emerald-950 text-white px-5 py-2.5 rounded-full font-semibold text-sm transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
            >
              Order now <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* ─── Recent order strip ─── */}
      {lastTrackingCode && (
        <div className="bg-emerald-900 text-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 flex items-center gap-3 text-sm">
            <Package size={15} className="text-emerald-200 shrink-0" />
            <p className="flex-1 min-w-0 truncate">
              Your recent order <span className="font-mono font-bold ml-1">{lastTrackingCode}</span>
              <span className="hidden sm:inline text-emerald-200/80 font-normal"> — tap to see status</span>
            </p>
            <button
              onClick={() => navigate(`/track?code=${lastTrackingCode}`)}
              className="flex items-center gap-1 font-semibold underline underline-offset-2 hover:text-white/90 shrink-0 cursor-pointer"
            >
              Track <ChevronRight size={14} />
            </button>
            <button onClick={dismissRecentOrder} aria-label="Dismiss recent order" className="text-white/60 hover:text-white shrink-0 cursor-pointer">
              <X size={14} />
            </button>
          </div>
        </div>
      )}

      {/* ─── Hero (centred editorial) ─── */}
      <section className="relative">
        <div className="max-w-4xl mx-auto px-6 text-center pt-16 pb-12 lg:pt-24 lg:pb-16">
          <p className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800 bg-emerald-50 border border-emerald-100 rounded-full px-3 py-1.5 mb-7">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-600" />
            Same-day Nairobi delivery · Pay on delivery
          </p>
          <h1 className="font-display font-black text-slate-900 text-[40px] sm:text-[58px] lg:text-[76px] leading-[1.03] tracking-[-0.03em]">
            Bring Fresh Onions
            <br className="hidden sm:block" /> To Every Kitchen.
          </h1>
          <p className="mt-6 text-slate-600 text-lg max-w-xl mx-auto leading-relaxed">
            Three grades of premium red onions, direct from Rift Valley growers
            to your kitchen, market stall, or restaurant — same day, pay on delivery.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/order')}
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-emerald-900 text-white font-semibold text-[15px] px-7 py-4 rounded-full transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
            >
              Get started <ArrowRight size={17} />
            </button>
            <a
              href="tel:+254790215267"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-slate-900 text-slate-900 font-semibold text-[15px] px-6 py-4 rounded-full transition-colors"
            >
              <Phone size={15} /> +254 790 215 267
            </a>
          </div>
        </div>

        {/* Full-bleed hero image band with overlay (reference signature) */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative rounded-[28px] overflow-hidden aspect-[16/10] sm:aspect-[16/8] lg:aspect-[16/6] bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900">
            {heroPhoto && (
              <img
                src={heroPhoto}
                alt="Fresh red onions sorted and ready for delivery across Nairobi"
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute left-5 right-5 bottom-5 sm:left-8 sm:right-8 sm:bottom-8 flex items-end justify-between gap-4">
              <h2 className="font-display text-white text-2xl sm:text-4xl font-semibold leading-tight max-w-md drop-shadow">
                From the farm to<br />your door.
              </h2>
              <button
                onClick={() => navigate('/order')}
                className="hidden sm:inline-flex items-center gap-2 bg-white/95 hover:bg-white text-slate-900 font-semibold text-sm px-5 py-3 rounded-full transition-colors cursor-pointer shrink-0"
              >
                Order fresh onions <ArrowUpRight size={15} />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Stats band ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 lg:py-20">
        <dl className="grid grid-cols-2 lg:grid-cols-4 gap-y-10 gap-x-6">
          {STATS.map((s, i) => (
            <div key={s.label} className={`px-2 ${i > 0 ? 'lg:border-l lg:border-slate-200' : ''}`}>
              <dt className="font-display font-black text-slate-900 text-4xl sm:text-5xl tracking-tight">{s.value}</dt>
              <dd className="text-xs sm:text-sm text-slate-500 uppercase tracking-widest mt-2">{s.label}</dd>
            </div>
          ))}
        </dl>
      </section>

      {/* ─── Statement + grade chips ─── */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-sm text-slate-400 font-medium">Since 2019</p>
            <div className="mt-6 flex flex-wrap gap-2">
              {['Direct from farm', 'Same-day delivery', 'Pay on delivery'].map((chip, i) => (
                <span
                  key={chip}
                  className={`text-sm font-medium rounded-full px-3.5 py-1.5 border ${
                    i === 0 ? 'bg-emerald-900 text-white border-emerald-900' : 'bg-white text-slate-700 border-slate-200'
                  }`}
                >
                  {chip}
                </span>
              ))}
            </div>
          </div>
          <div className="lg:col-span-8">
            <h2 className="font-display text-slate-900 text-[28px] sm:text-4xl lg:text-[46px] font-semibold leading-[1.1] tracking-[-0.02em]">
              Most onion buyers still queue at Gikomba before dawn. We bring the
              wholesale market straight to your door — fresh, graded, and fairly priced.
            </h2>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm font-medium text-slate-600">
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700" /> No brokers, no markups</span>
              <span className="inline-flex items-center gap-2"><CheckCircle2 size={16} className="text-emerald-700" /> Replaced or refunded if substandard</span>
            </div>
          </div>
        </div>
      </section>

      {/* ─── "Get started" gallery with arrows ─── */}
      <section id="products" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="flex items-end justify-between gap-6 mb-10">
          <div>
            <h2 className="font-display text-slate-900 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight">
              Get started now
            </h2>
            <p className="text-slate-600 mt-3 max-w-md">
              Pick a grade and we'll do the rest. Better tier rates unlock on bigger orders.
            </p>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <button
              onClick={() => scrollGallery(-1)}
              aria-label="Previous products"
              className="w-11 h-11 rounded-full border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => scrollGallery(1)}
              aria-label="Next products"
              className="w-11 h-11 rounded-full border border-slate-300 hover:border-slate-900 hover:bg-slate-900 hover:text-white text-slate-700 flex items-center justify-center transition-colors cursor-pointer"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div
          ref={galleryRef}
          className="flex gap-5 overflow-x-auto pb-4 snap-x snap-mandatory scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
        >
          {GALLERY.map((g, idx) => {
            const photo = productPhotos[g.id] || GRADE_IMAGES[g.id] || LANDING_IMAGES.market;
            return (
              <button
                key={`${g.id}-${idx}`}
                onClick={() => navigate(`/order?p=${g.id}`)}
                className="group relative shrink-0 snap-start w-[78%] sm:w-[360px] aspect-[5/6] rounded-3xl overflow-hidden border border-slate-200 text-left cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
              >
                {photo ? (
                  <img src={photo} alt={g.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]" />
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-100 via-white to-amber-50 flex items-center justify-center">
                    <span className="font-display font-black text-7xl text-emerald-900/20">{g.no}</span>
                  </div>
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div className="absolute left-4 top-4">
                  <span className="font-mono text-xs font-semibold text-white/90 bg-black/30 backdrop-blur px-2 py-1 rounded-md">{g.no}</span>
                </div>
                <div className="absolute left-4 right-4 bottom-4 flex items-end justify-between gap-3">
                  <div>
                    <p className="text-white font-semibold text-lg leading-tight drop-shadow">{g.label}</p>
                    <p className="text-white/80 text-xs mt-0.5">{g.caption}</p>
                  </div>
                  <span className="w-9 h-9 rounded-full bg-white/95 text-slate-900 flex items-center justify-center shrink-0 group-hover:bg-emerald-500 group-hover:text-white transition-colors">
                    <ArrowUpRight size={16} />
                  </span>
                </div>
              </button>
            );
          })}
        </div>

        {/* Tier pricing strip */}
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { label: `Under ${pricing.tier1MaxKg} kg`, price: pricing.tier1Price },
            { label: `${pricing.tier1MaxKg}–${pricing.tier2MaxKg} kg`, price: pricing.tier2Price },
            { label: `Above ${pricing.tier2MaxKg} kg`, price: pricing.tier3Price, best: true },
          ].map((tier) => (
            <div
              key={tier.label}
              className={`rounded-2xl px-5 py-4 flex items-center justify-between border ${
                tier.best ? 'bg-emerald-900 border-emerald-900 text-white' : 'bg-white border-slate-200 text-slate-900'
              }`}
            >
              <p className={`text-xs font-semibold uppercase tracking-widest ${tier.best ? 'text-emerald-200' : 'text-slate-500'}`}>
                {tier.label}
              </p>
              <p className="font-display font-black text-2xl">
                KSh {tier.price}
                <span className={`text-xs font-medium ${tier.best ? 'text-emerald-200' : 'text-slate-400'}`}> /kg</span>
              </p>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-500 mt-3 text-center">Minimum order {pricing.minOrderKg} kg · Cash or M-Pesa on delivery</p>
      </section>

      {/* ─── Trust / brand band ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-4">
        <div className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-emerald-900 via-emerald-800 to-emerald-950 px-7 py-12 sm:px-12 sm:py-16">
          <img src={LANDING_IMAGES.field} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-20 mix-blend-luminosity" />
          <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/90 via-emerald-800/85 to-emerald-950/95" />
          <div className="relative max-w-2xl">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-widest text-emerald-200 bg-white/10 border border-white/15 rounded-full px-3 py-1.5">
              <MapPin size={12} /> Nairobi, Kenya
            </span>
            <h2 className="font-display text-white text-3xl sm:text-5xl font-semibold leading-[1.08] tracking-tight mt-6">
              Trusted by kitchens, stalls and restaurants across the city.
            </h2>
            <p className="text-emerald-100/90 mt-5 max-w-lg leading-relaxed">
              From a single 5 kg order to weekly restaurant supply — same quality,
              same fair price, delivered the same day.
            </p>
          </div>
          <p className="absolute right-7 bottom-7 sm:right-12 sm:bottom-12 font-mono text-emerald-200/70 text-sm">500+ clients</p>
        </div>
      </section>

      {/* ─── Feature cards ("Next-Gen Solutions") ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-12 gap-6 items-end mb-12">
          <h2 className="lg:col-span-7 font-display text-slate-900 text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.08]">
            Everything you need for a fuss-free onion supply
          </h2>
          <p className="lg:col-span-5 text-slate-600 leading-relaxed">
            We handle sourcing, grading, and delivery so you can focus on cooking,
            selling, or serving — never on chasing stock again.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, img, title, body }) => (
            <div
              key={title}
              className="group rounded-3xl border border-slate-200 overflow-hidden bg-white hover:shadow-xl hover:shadow-emerald-900/5 hover:border-emerald-700/40 transition-all"
            >
              <div className="relative aspect-[16/10] overflow-hidden border-b border-slate-100">
                <img
                  src={img}
                  alt={title}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
                <div className="absolute left-4 bottom-4 w-12 h-12 rounded-2xl bg-white/95 text-emerald-900 flex items-center justify-center shadow-sm">
                  <Icon size={22} strokeWidth={1.75} />
                </div>
              </div>
              <div className="p-7">
                <h3 className="font-display font-semibold text-slate-900 text-xl tracking-tight">{title}</h3>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ─── How it works statement ─── */}
      <section id="how" className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden aspect-[4/3] bg-gradient-to-br from-emerald-100 via-white to-amber-50 border border-slate-200">
              <img src={LANDING_IMAGES.market} alt="Graded red onions in a crate, ready for dispatch" className="absolute inset-0 w-full h-full object-cover" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <h2 className="font-display text-slate-900 text-3xl sm:text-4xl lg:text-[44px] font-semibold leading-[1.1] tracking-tight">
              Changing how Nairobi buys onions — fair prices, fresh stock, same-day.
            </h2>
            <div className="mt-8 space-y-5">
              {HOW_IT_WORKS.map((s) => (
                <div key={s.step} className="flex gap-4">
                  <span className="font-mono text-sm text-emerald-700 font-semibold pt-1 shrink-0">{s.step}</span>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-lg">{s.title}</h3>
                    <p className="text-slate-600 text-sm mt-1 leading-relaxed">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ─── Delivery zones ─── */}
      <section id="zones" className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800 mb-3">Coverage</p>
            <h2 className="font-display text-slate-900 text-3xl sm:text-4xl font-semibold tracking-tight">
              Delivered across Nairobi.
            </h2>
            <p className="text-slate-600 mt-5 leading-relaxed">
              34+ zones covered. Same-day for orders placed before 10 AM.
              Buruburu is free delivery; elsewhere a small KSh 25/km fee applies.
            </p>
            <a
              href="tel:+254790215267"
              className="inline-flex items-center gap-2 mt-7 text-emerald-900 font-semibold hover:underline"
            >
              <Phone size={15} /> Call to check your area
            </a>
          </div>

          <div className="lg:col-span-7">
            <div className="border-t border-slate-200 pt-6">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Primary zones</p>
              <ul className="flex flex-wrap gap-2">
                {ZONES_PRIMARY.map(z => (
                  <li key={z} className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-900 border border-emerald-100 rounded-full px-3.5 py-1.5 text-sm font-medium">
                    <MapPin size={12} /> {z}
                  </li>
                ))}
              </ul>
            </div>
            <div className="border-t border-slate-200 pt-6 mt-8">
              <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-4">Also delivering to</p>
              <ul className="flex flex-wrap gap-2">
                {ZONES_SECONDARY.map(z => (
                  <li key={z} className="inline-flex items-center bg-white text-slate-700 border border-slate-200 rounded-full px-3.5 py-1.5 text-sm">
                    {z}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ─── Final CTA band ─── */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-20">
        <div className="relative rounded-[28px] overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-700 to-emerald-900 px-7 py-16 sm:px-12 sm:py-20 text-center">
          <Clock size={26} className="mx-auto text-emerald-200 mb-5" />
          <h2 className="font-display text-white text-3xl sm:text-5xl font-semibold tracking-tight leading-[1.08] max-w-2xl mx-auto">
            Order fresh onions today.
          </h2>
          <p className="text-emerald-100/90 mt-4 max-w-md mx-auto leading-relaxed">
            Online or by phone — either way, fresh red onions at your door by tonight.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/order')}
              className="inline-flex items-center gap-2 bg-white text-emerald-900 font-bold text-[15px] px-7 py-4 rounded-full hover:bg-emerald-50 transition-colors cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              Order online <ArrowRight size={16} />
            </button>
            <a
              href="tel:+254790215267"
              className="inline-flex items-center gap-2 border border-emerald-200/40 hover:border-white text-white font-semibold text-[15px] px-6 py-4 rounded-full transition-colors"
            >
              <Phone size={15} /> Call to order
            </a>
          </div>
        </div>
      </section>

      {/* ─── Footer ─── */}
      <footer className="bg-slate-950 text-slate-400">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14 grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="/brand/logo-icon.png" alt="Genvitungu" className="w-9 h-9 rounded-lg object-cover" />
              <p className="text-white font-display font-bold tracking-tight text-lg">Genvitungu</p>
            </div>
            <p className="text-sm leading-relaxed">
              Fresh red onions from Rift Valley farmers, delivered across Nairobi.
            </p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Shop</p>
            <ul className="space-y-2 text-sm">
              <li><Link to="/order" className="hover:text-white transition-colors">Place an order</Link></li>
              <li><Link to="/track" className="hover:text-white transition-colors">Track an order</Link></li>
              <li><a href="#products" className="hover:text-white transition-colors">Our grades</a></li>
              <li><a href="#zones" className="hover:text-white transition-colors">Delivery zones</a></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Company</p>
            <ul className="space-y-2 text-sm">
              <li><a href="#how" className="hover:text-white transition-colors">How it works</a></li>
              <li><Link to="/supply" className="hover:text-white transition-colors">For businesses — supply plans</Link></li>
              <li><a href="tel:+254790215267" className="hover:text-white transition-colors">Same-day delivery</a></li>
              <li><Link to="/admin" className="hover:text-white transition-colors">Staff login</Link></li>
            </ul>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-3">Contact</p>
            <ul className="space-y-2 text-sm">
              <li><a href="tel:+254790215267" className="hover:text-white transition-colors">+254 790 215 267</a></li>
              <li><a href="https://wa.me/254790215267" className="hover:text-white transition-colors">WhatsApp us</a></li>
              <li className="text-slate-500">Mon–Sat · 7 AM – 7 PM</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-900">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs text-slate-500">© {new Date().getFullYear()} Genvitungu. All rights reserved.</p>
            <p className="text-xs text-slate-600">{PHOTO_CREDITS}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
